/* global axios */
import ApiClient from 'dashboard/api/ApiClient';

// Browser UTC offset in hours (e.g. -3 for America/Sao_Paulo), matching how the native
// Chatwoot reports send it. The backend buckets days in this timezone so the report's
// day axis follows the viewer's local day rather than UTC.
const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

// The computed browser offset always wins: spread the caller params first so a stale or
// caller-supplied timezone_offset can't override the real local offset.
const withTimezone = (params = {}) => ({
  ...params,
  timezone_offset: getTimeOffset(),
});

class KanbanReportsAPI extends ApiClient {
  constructor() {
    super('kanban/reports', { accountScoped: true, apiVersion: 'v1' });
  }

  summary(params = {}) {
    return axios.get(`${this.url}/summary`, { params: withTimezone(params) });
  }

  timeseries(params = {}) {
    return axios.get(`${this.url}/timeseries`, {
      params: withTimezone(params),
    });
  }

  funnel(params = {}) {
    return axios.get(`${this.url}/funnel`, { params: withTimezone(params) });
  }

  cfd(params = {}) {
    return axios.get(`${this.url}/cfd`, { params: withTimezone(params) });
  }

  aging(params = {}) {
    return axios.get(`${this.url}/aging`, { params: withTimezone(params) });
  }

  agentPerformance(params = {}) {
    return axios.get(`${this.url}/agent_performance`, {
      params: withTimezone(params),
    });
  }

  stageDurations(params = {}) {
    return axios.get(`${this.url}/stage_durations`, {
      params: withTimezone(params),
    });
  }

  dimension(params = {}) {
    return axios.get(`${this.url}/dimension`, { params: withTimezone(params) });
  }

  // Fetches the CSV variant of a report (the download button). `report` is the endpoint
  // segment (e.g. 'summary', 'agent_performance'). Resolves a Blob: decoding it as text would
  // drop the BOM that makes Excel read the accents.
  download(report, params = {}) {
    return axios
      .get(`${this.url}/${report}.csv`, {
        params: withTimezone(params),
        responseType: 'blob',
      })
      .then(response => response.data);
  }

  // Lista de cartoes (xlsx/csv). Devolve a resposta inteira: 200 traz o arquivo, 202 avisa que ele vai por e-mail.
  // O timezone_offset so serve ao periodo; as datas do arquivo saem no fuso da conta.
  exportTasks(params = {}) {
    // No corpo JSON o periodo vai como texto, igual aos GET: o servidor recusa epoch em numero.
    const period = Object.fromEntries(
      ['since', 'until']
        .filter(key => params[key] != null)
        .map(key => [key, String(params[key])])
    );
    return axios.post(
      `${this.url}/tasks_export`,
      withTimezone({ ...params, ...period }),
      {
        responseType: 'blob',
      }
    );
  }
}

export default new KanbanReportsAPI();
